import { useGetEvents } from "@package/ui-apollo-components";
import {
  EventPayloadType,
  type Event,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import { useWallAnalytics, useWallConfig } from "@package/ui-wall";
import { type SliceWallScreenState } from "@package/ui-wall-engine";
import moment from "moment";
import {
  useCallback,
  useMemo,
  useReducer,
  useRef,
  type PropsWithChildren,
} from "react";
import { useContainerSize } from "../../utils/use-container-size";
import { TriviaContext, type TriviaContextState } from "./context-trivia";
import { parseTriviaConfig } from "./trivia-parse";
import {
  createInitialTriviaMachineState,
  triviaReducer,
} from "./trivia-reducer";
import { computeScore } from "./trivia-score";
import { createPlaythrough } from "./trivia-shuffle";
import type { TriviaAnswerModel, TriviaHelp } from "./trivia-types";

type TriviaProviderProps = {
  screen?: SliceWallScreenState;
  disableAnalytics?: boolean;
};

export function TriviaContextProvider({
  screen,
  disableAnalytics,
  children,
}: PropsWithChildren<TriviaProviderProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const lockRef = useRef<string | undefined>(undefined);
  const containerSize = useContainerSize(ref);
  const { trigger } = useWallAnalytics();
  const { clientAnalytics } = useWallConfig();

  const post = screen?.flyout?.post;
  const postId = post?.id;
  const layoutId = screen?.layout?.id;

  const { config, issues } = useMemo(
    () =>
      parseTriviaConfig(post?.customData, {
        fallbackTitle: post?.title ?? undefined,
      }),
    [post?.customData, post?.title],
  );

  // The seed lives solely in reducer state, chosen once via the lazy initializer (invoked
  // once per mount; React's StrictMode double-invoke discards one of the two results, so the
  // value actually used is stable). createPlaythrough is a pure function of (config, seed), so
  // re-running the memo below — StrictMode, memo eviction — always reproduces the same deck.
  const [machine, dispatch] = useReducer(triviaReducer, undefined, () =>
    createInitialTriviaMachineState(config, Date.now()),
  );

  const playthrough = useMemo(
    () => createPlaythrough(config, machine.seed),
    [config, machine.seed],
  );

  const currentQuestion = playthrough.questions[machine.questionIndex];
  const totalQuestions = playthrough.questions.length;

  const score = useMemo(
    () => computeScore(playthrough, machine.selections, config),
    [playthrough, machine.selections, config],
  );

  const analyticsNeeded =
    config.feedback.showDistribution || config.results.showChart;
  const skipAnalytics =
    disableAnalytics ||
    !analyticsNeeded ||
    (machine.phase !== "feedback" && machine.phase !== "result");

  const variablesEvents = useMemo<GetEventsQueryVariables>(
    () => ({
      page: 0,
      perPage: 1000,
      sortField: "timestamp",
      sortOrder: "desc",
      filter: {
        eventType: [EventPayloadType.TriviaQuestion],
        post: postId ? [postId] : [],
        range: {
          end: moment().toDate(),
          start: moment().subtract(7, "days").toDate(),
        },
      },
    }),
    [postId],
  );

  const { data: dataEvents, loading: analyticsLoading } = useGetEvents(
    variablesEvents,
    skipAnalytics,
    clientAnalytics,
  );

  const analytics = (dataEvents?.items ?? []) as Event[];

  const start = useCallback(() => {
    dispatch({ type: "start" });
  }, []);

  const submitAnswer = useCallback(
    (answer: TriviaAnswerModel) => {
      if (!currentQuestion || lockRef.current === currentQuestion.id) {
        return;
      }

      lockRef.current = currentQuestion.id;

      dispatch({
        type: "answer",
        questionId: currentQuestion.id,
        answerId: answer.id,
        correct: answer.correct,
        points: answer.correct ? currentQuestion.points : 0,
        totalQuestions,
        skipFeedback: !config.feedback.enabled,
      });

      trigger(
        EventPayloadType.TriviaQuestion,
        {
          postId,
          question: currentQuestion.prompt,
          answer: answer.label,
          correct: answer.correct,
        },
        postId ?? undefined,
        layoutId ?? undefined,
      );
    },
    [
      currentQuestion,
      totalQuestions,
      config.feedback.enabled,
      trigger,
      postId,
      layoutId,
    ],
  );

  const submitTimeout = useCallback(() => {
    if (!currentQuestion || lockRef.current === currentQuestion.id) {
      return;
    }

    lockRef.current = currentQuestion.id;

    dispatch({
      type: "timeout",
      questionId: currentQuestion.id,
      totalQuestions,
      skipFeedback: !config.feedback.enabled,
    });
  }, [currentQuestion, totalQuestions, config.feedback.enabled]);

  const advance = useCallback(() => {
    lockRef.current = undefined;
    dispatch({ type: "advance", totalQuestions });
  }, [totalQuestions]);

  const activateHelp = useCallback((help: TriviaHelp) => {
    dispatch({ type: "help", help });
  }, []);

  const state: TriviaContextState = {
    screen,
    config,
    issues,
    playthrough,
    currentQuestion,
    machine,
    score,
    containerSize,
    analytics,
    analyticsLoading,
    start,
    submitAnswer,
    submitTimeout,
    advance,
    activateHelp,
  };

  return (
    <TriviaContext.Provider value={state}>
      <div ref={ref} className="relative h-full w-full">
        {children}
      </div>
    </TriviaContext.Provider>
  );
}
